import React, { Suspense, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { Menu, ChevronRight, ChevronDown, LogOut, Lightbulb, Mail, BookOpen } from 'lucide-react';
import { visibleNavItems } from '@/lib/navItems';
import { pageKey, reportFromSearch } from '@/lib/reportList';
import { GUIDE_URL, GUIDE_FILENAME, GUIDE_LABEL } from '@/lib/guideDocument';
import FeedbackDialog from './FeedbackDialog';
import EmailPreferencesDialog from './EmailPreferencesDialog';
import ErrorBoundary from './ErrorBoundary';


/* Shown in the page area while a page's file is on its way - usually a
   fraction of a second, once per page per visit. */
function PageLoading() {
  return (
    <div role="status" aria-label="Loading page" className="flex justify-center py-24">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [emailPrefsOpen, setEmailPrefsOpen] = useState(false);
  /* Dropdowns opened or closed by hand, by path. One left out follows the
     page: open while you are on it, closed otherwise. */
  const [expanded, setExpanded] = useState({});

  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me(),
    staleTime: 60_000,
  });

  /* Which pages this tier may see lives in @/lib/navItems, with the test
     that pins it. */
  const navItems = visibleNavItems(currentUser?.role);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-sidebar text-sidebar-foreground
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:z-auto
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Brand. The wordmark is brand red on transparent, so it sits on its
              own white bar rather than directly on the navy sidebar. */}
          <div className="bg-white px-6 py-4 border-b border-sidebar-border">
            <Link to="/MyDay" className="block">
              <img
                src="/westwood-logo.png"
                alt="Westwood Financial"
                className="h-7 w-auto"
              />
              <p className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-primary/70">Project Hub</p>
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map(item => {
              const isActive = location.pathname.startsWith(item.path);
              if (item.children) {
                /* A dropdown (Reports). Open by itself while you are on one of
                   its pages, so the report you are reading is always marked;
                   the arrow closes or opens it by hand either way. */
                const isOpen = expanded[item.path] ?? isActive;
                const activeKey = isActive ? reportFromSearch(location.search).key : null;
                const listId = `nav-children-${item.label.toLowerCase()}`;
                return (
                  <div key={item.path}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={listId}
                      onClick={() => setExpanded(e => ({ ...e, [item.path]: !isOpen }))}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all w-full
                        ${isActive
                          ? 'text-sidebar-foreground'
                          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'}
                      `}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                      <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                    </button>
                    {isOpen && (
                      <div id={listId} className="mt-1 ml-4 pl-3 border-l border-sidebar-border space-y-1">
                        {item.children.map(child => {
                          const childActive = child.key === activeKey;
                          return (
                            <Link
                              key={child.key}
                              to={child.path}
                              aria-current={childActive ? 'page' : undefined}
                              onClick={() => setMobileOpen(false)}
                              className={`
                                flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all
                                ${childActive
                                  ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'}
                              `}
                            >
                              <child.icon className="w-3.5 h-3.5" />
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                    ${isActive
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'}
                  `}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-3 py-4 border-t border-sidebar-border space-y-1">
            {/* The guide is a file, not a route, so this is an anchor rather
                than a Link -- and it is open to every tier, including the
                read-only one: reading how the Hub works is not changing
                anything.

                `download` is what names the saved file: the URL is terse and
                somebody who saves this wants to find it again next to the copy
                that was emailed to them. It is not what makes the download
                work -- the file is there and would be served either way.

                The sidebar is closed on click for the same reason the nav
                links above do it. A download does not change the route, so on
                a phone the sidebar and its overlay would otherwise stay open
                across the page with nothing to dismiss them. */}
            <a
              href={GUIDE_URL}
              download={GUIDE_FILENAME}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 w-full transition-all"
            >
              <BookOpen className="w-4 h-4" />
              {GUIDE_LABEL}
            </a>
            <button
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 w-full transition-all"
            >
              <Lightbulb className="w-4 h-4" />
              Suggest an Improvement
            </button>
            <button
              onClick={() => setEmailPrefsOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 w-full transition-all"
            >
              <Mail className="w-4 h-4" />
              Email Preferences
            </button>
            <button
              onClick={() => base44.auth.logout('/MyDay')}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 w-full transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
          <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
          <EmailPreferencesDialog open={emailPrefsOpen} onClose={() => setEmailPrefsOpen(false)} />
        </div>
      </aside>

      {/* Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b border-border px-4 lg:px-8 h-14 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Open the menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1" />
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {/* Keyed on the location so navigating away from a crashed page
              clears the error by itself; the navigation stays usable.
              Switching between reports is not a new page - see pageKey. */}
          <ErrorBoundary key={pageKey(location)} variant="content">
            {/* Pages are fetched on first visit (React.lazy in App.jsx). The
                boundary is here, inside the frame, so the sidebar and header
                stay put while a page's file arrives; a file that fails to
                arrive reaches the ErrorBoundary above. */}
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}